import React, { useEffect } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { Radio } from '@atlaskit/radio';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import {
  FormValues,
  ChampionPreference,
  ChampionPicklistField,
  SUCCESS_CHAMPION_PREFERENCES,
} from '../../../types/ChampionApplicationFormTypes';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import { getEnvironmentAsEnum } from '../../../utils/environment';

type FieldErrors = {
  championPreference?: string;
};

// These options exist in Salesforce but are not enabled for signup
const DISABLED_CONTRIBUTION_TYPES = new Set(['Certification SME', 'Community Speaker']);

type Props = {
  values: FormValues;
  updateField: <K extends keyof FormValues>(key: K, value: FormValues[K]) => void;
  accountIsManaged?: boolean | null;
  errors?: FieldErrors;
  contributionTypeOptions: ChampionPicklistField['picklistValues'];
};

const PREFERENCE_OPTIONS: {
  id: NonNullable<ChampionPreference>;
  label: string;
  group: 'community' | 'success';
}[] = [
  {
    id: 'Community Forums Contributor',
    label: 'Contributing to Atlassian Community Forums',
    group: 'community',
  },
  {
    id: 'Community Event Organizer',
    label: 'Organizing Atlassian Community Events',
    group: 'community',
  },
  {
    id: 'Creator',
    label: 'Creating original thought leadership or learning content for social media',
    group: 'community',
  },
  {
    id: 'Trainer/Mentor',
    label: "Creating or delivering Atlassian training for your organization's end users",
    group: 'success',
  },
  {
    id: 'CUG Leader',
    label: 'Running Atlassian-focused training events or a Company User Group in your organization',
    group: 'success',
  },
  {
    id: 'CoE Member',
    label: 'Contributing Atlassian knowledge to a Center of Excellence in your organization',
    group: 'success',
  },
];

export const PreferenceSection: React.FC<Props> = ({
  values,
  updateField,
  accountIsManaged,
  errors,
  contributionTypeOptions,
}) => {
  const selected = values.championPreference;

  const handleChange = (id: ChampionPreference) => () => updateField('championPreference', id);

  const enabledValues = new Set(
    contributionTypeOptions
      .map((o) => o.value)
      .filter((v) => !DISABLED_CONTRIBUTION_TYPES.has(v)),
  );

  const activeOptions = PREFERENCE_OPTIONS.filter((o) => enabledValues.has(o.id));
  const communityOptions = activeOptions.filter((o) => o.group === 'community');
  const successOptions = activeOptions.filter((o) => o.group === 'success');

  const isSuccessDisabled = accountIsManaged === false || accountIsManaged === null;

  const switchAccountUrl = getCommunityLoginUrl(
    {
      redirectUrl: `${process.env.HOME_BASE_URL}/champions/apply`,
      experience: 'entry-auth',
      prompt: 'select_account',
    },
    getEnvironmentAsEnum(),
  );

  useEffect(() => {
    if (isSuccessDisabled && selected && SUCCESS_CHAMPION_PREFERENCES.includes(selected)) {
      updateField('championPreference', null);
    }
  }, [isSuccessDisabled, selected, updateField]);

  return (
    <div css={sectionBoxStyles} className="champion-form-preference">
      <div css={headingLabelStyles} className="champion-form-preference-heading">
        How do you want to contribute as an Atlassian Champion? Select your top preference
      </div>

      <div css={controlTopStyles}>
        <p css={groupTitleStyles}>
          Community Champion
          <FieldHelpTooltip
            content="Your selected contribution type will be used to assess your eligibility and ongoing champion status. But you can still contribute to the community in multiple ways."
            iconLabel="Community Champion Help Info"
          />
        </p>
        {communityOptions.map((opt) => (
          <Radio
            key={opt.id}
            name="champion_preference"
            label={opt.label}
            value={opt.id}
            isChecked={selected === opt.id}
            onChange={handleChange(opt.id)}
          />
        ))}

        {isSuccessDisabled && (
          <div css={successMessageStyles} className="champion-form-success-message">
            <span css={successMessageIconStyles} aria-hidden="true">
              <svg viewBox="0 0 16 16" width="16" height="16" fill="none" focusable="false">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM6.5 6.75V8.25H7.25V12.5H8.75V7.5C8.75 7.08579 8.41421 6.75 8 6.75H6.5ZM8 3.5C7.44772 3.5 7 3.94772 7 4.5C7 5.05228 7.44772 5.5 8 5.5C8.55228 5.5 9 5.05228 9 4.5C9 3.94772 8.55228 3.5 8 3.5Z"
                  fill="#AF59E1"
                />
              </svg>
            </span>
            <span css={successMessageTextStyles}>
              <a href={switchAccountUrl} css={inlineLinkStyles}>
                Switch to an Atlassian account
              </a>
              {' '}
              that uses an organization email to apply to be a Success Champion.
            </span>
          </div>
        )}

        <div
          className={isSuccessDisabled ? 'champion-form-success-washed-out' : undefined}
          css={[successBlockStyles, isSuccessDisabled && successBlockWithMessageAboveStyles]}
        >
          <p css={groupTitleTopStyles}>
            Success Champion (join waitlist)
            <FieldHelpTooltip
              content="The Success Champion program is in Beta. Eligibility and participation are currently limited. By selecting this track, you'll join our waitlist."
              iconLabel="Success Champion Help Info"
            />
          </p>
          {successOptions.map((opt) => (
            <Radio
              key={opt.id}
              name="champion_preference"
              label={opt.label}
              value={opt.id}
              isChecked={selected === opt.id}
              onChange={handleChange(opt.id)}
              isDisabled={isSuccessDisabled}
            />
          ))}
        </div>
      </div>

      {errors?.championPreference && <div css={errorStyles}>{errors.championPreference}</div>}
    </div>
  );
};

const sectionBoxStyles = css({
  marginBottom: '40px',
});

const headingLabelStyles = css({
  fontWeight: 600,
  fontSize: '10pt',
  fontFamily: 'sans-serif',
  color: 'black',
  lineHeight: 1.5,
});

const controlTopStyles = css({
  marginTop: '16px',
});

const groupTitleStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.5em', /* half line between heading and options (max one line) */
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
});

const successBlockStyles = css({
  display: 'block',
  paddingTop: '16px', /* gap from Community block when message not visible */
});

const successBlockWithMessageAboveStyles = css({
  paddingTop: 0, /* message provides 8px gap; no extra padding needed */
});

const successMessageStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  maxWidth: '660px',
  minHeight: '20px',
  marginTop: '16px',
  marginBottom: '8px',
});

const successMessageIconStyles = css({
  display: 'inline-flex',
  flexShrink: 0,
  color: 'var(--champion-form-purple)',
  '& svg': {
    width: '16px',
    height: '16px',
  },
});

const successMessageTextStyles = css({
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
});

const inlineLinkStyles = css({
  color: 'var(--champion-form-blue)',
  textDecoration: 'none',
});

const groupTitleTopStyles = css({
  fontWeight: 400,
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  color: '#696969',
  marginBottom: '0.5em', /* half line between heading and options (max one line) */
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});
